import DocumentGroupEditBody from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentGroupEditBody';
import DocumentSettings from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings';
import WorkflowFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/WorkflowFormData';
import WorkflowDraftProvider from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider';
import documentSettingsScreenSignal, {
	closeDocumentSettingsScreen,
} from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Signals/documentSettingsScreenSignal';
import type OrganizationWorkflowView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowView';
import * as React from 'react';
import { ItemBreadcrumb } from '@verclam/breadcrumbs';
import { useSignalValue } from '@verclam/pulsar';

type Props = {
	/** Null when composing a new group. */
	readonly workflow: OrganizationWorkflowView | null;
	readonly organizationId: number;
};

/**
 * The editor of one group, made of two screens that share one draft: the group form and, when a
 * document's settings are open, that document's settings screen. The draft starts from the group's
 * current version and is never re-seeded: the parent re-keys this component on the version, so a
 * published save starts a clean draft.
 */
const DocumentGroupEditor: React.FC<Props> = ({ workflow, organizationId }) => {
	const [initialDraft] = React.useState(() =>
		workflow !== null ? WorkflowFormData.fromView(workflow) : WorkflowFormData.createEmpty(),
	);
	const openDocument = useSignalValue(documentSettingsScreenSignal);

	React.useEffect(() => closeDocumentSettingsScreen, []);

	return (
		<WorkflowDraftProvider initialDraft={initialDraft}>
			<ItemBreadcrumb label={workflow?.label ?? 'Nouveau groupe'} />
			{openDocument !== null ? (
				<DocumentSettings stageIndex={openDocument.stageIndex} stepIndex={openDocument.stepIndex} />
			) : (
				<DocumentGroupEditBody workflow={workflow} organizationId={organizationId} />
			)}
		</WorkflowDraftProvider>
	);
};

export default DocumentGroupEditor;
